import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import {
  Users,
  FileText,
  Receipt,
  CurrencyEur,
  MapPin,
  Globe,
  Clock,
  Tree,
  Buildings,
  Calendar,
  ChartLineUp,
  ArrowRight,
  Shield,
  House,
  Share,
  Check,
  Copy,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Animated Counter (count-up on scroll) ----------
function CountUp({ target, duration = 1600, suffix = '', prefix = '', decimals = 0 }) {
  const [value, setValue] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const t = target || 0;
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(t * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  const formatted = decimals === 0
    ? Math.round(value).toLocaleString('it-IT')
    : value.toLocaleString('it-IT', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
}

// ---------- Fade-in wrapper ----------
function FadeIn({ children, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {children}
    </div>
  );
}

// ---------- KPI Card ----------
function KpiCard({ icon: Icon, value, suffix = '', label, accent = 'zinc', prefix = '', decimals = 0, delay = 0 }) {
  const accents = {
    zinc: { ring: 'hover:border-zinc-900', dot: 'bg-zinc-900', icon: 'text-zinc-700' },
    emerald: { ring: 'hover:border-emerald-500', dot: 'bg-emerald-500', icon: 'text-emerald-600' },
    rose: { ring: 'hover:border-rose-500', dot: 'bg-rose-500', icon: 'text-rose-600' },
    indigo: { ring: 'hover:border-indigo-500', dot: 'bg-indigo-500', icon: 'text-indigo-600' },
    amber: { ring: 'hover:border-amber-500', dot: 'bg-amber-500', icon: 'text-amber-600' },
  };
  const a = accents[accent] || accents.zinc;
  return (
    <FadeIn delay={delay}>
      <div className={`relative bg-white border border-zinc-200 p-5 sm:p-6 h-full transition-all duration-500 ${a.ring} hover:-translate-y-0.5`}>
        <div className="flex items-start justify-between mb-4">
          <Icon size={22} weight="duotone" className={a.icon} />
          <span className={`w-1.5 h-1.5 ${a.dot} opacity-60`} />
        </div>
        <p className="text-3xl sm:text-4xl font-black text-zinc-900 tracking-tight leading-none">
          <CountUp target={value} prefix={prefix} suffix={suffix} decimals={decimals} />
        </p>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500 mt-2 leading-snug">
          {label}
        </p>
      </div>
    </FadeIn>
  );
}

// ---------- Share Block (Web Share API + clipboard fallback) ----------
function ShareBlock({ stats }) {
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/stats`
    : 'https://contratti.vitadahost.it/stats';

  const shareText = `Gli host italiani di Locazione Turistica hanno già gestito €${Math.round(stats.impact.managed_revenue).toLocaleString('it-IT')}, firmato ${stats.contracts.total} contratti e risparmiato ${stats.impact.hours_saved}+ ore. Guarda i numeri reali:`;

  const handleShare = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title: 'Host italiani in numeri — Locazione Turistica',
          text: shareText,
          url: shareUrl,
        });
      } catch (e) {
        // user cancelled — silent
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 2600);
      } catch (e) {
        // very old browser fallback
        const ta = document.createElement('textarea');
        ta.value = `${shareText} ${shareUrl}`;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
        setCopied(true);
        setTimeout(() => setCopied(false), 2600);
      }
    }
  };

  // Direct share channels (work on both mobile & desktop)
  const encodedText = encodeURIComponent(`${shareText} ${shareUrl}`);
  const channels = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encodedText}`,
      color: 'hover:bg-emerald-500 hover:text-white hover:border-emerald-500',
    },
    {
      id: 'telegram',
      label: 'Telegram',
      href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
      color: 'hover:bg-sky-500 hover:text-white hover:border-sky-500',
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      color: 'hover:bg-blue-700 hover:text-white hover:border-blue-700',
    },
    {
      id: 'x',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${encodedText}`,
      color: 'hover:bg-zinc-100 hover:text-zinc-900 hover:border-zinc-100',
    },
  ];

  return (
    <FadeIn>
      <div className="bg-zinc-50 border border-zinc-200 p-6 sm:p-10" data-testid="share-block">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-600 mb-2">
              Sei fiero di questi numeri?
            </p>
            <h3 className="text-xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight mb-2">
              Condividi la pagina con gli host che conosci
            </h3>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-lg">
              Più siamo, più possiamo ottenere come community. Un tap e li aiuti a scoprire una piattaforma fatta per loro.
            </p>
          </div>

          <div className="flex flex-col gap-3 items-stretch md:items-end">
            {/* Primary share button (native on mobile, copy on desktop) */}
            <button
              onClick={handleShare}
              className={`group inline-flex items-center justify-center gap-2 font-black uppercase tracking-wider text-xs px-6 py-4 transition-all whitespace-nowrap ${
                copied
                  ? 'bg-emerald-500 text-white'
                  : 'bg-zinc-900 text-white hover:bg-zinc-800'
              }`}
              data-testid="share-main-btn"
              aria-label={canNativeShare ? 'Condividi' : 'Copia link'}
            >
              {copied ? (
                <>
                  <Check size={16} weight="bold" />
                  Copiato negli appunti
                </>
              ) : canNativeShare ? (
                <>
                  <Share size={16} weight="bold" />
                  Condividi la pagina
                  <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </>
              ) : (
                <>
                  <Copy size={16} weight="bold" />
                  Copia link + testo
                </>
              )}
            </button>

            {/* Quick channel icons — desktop focused */}
            <div className="flex items-center gap-1.5 justify-center md:justify-end" aria-label="Canali rapidi">
              {channels.map((c) => (
                <a
                  key={c.id}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center justify-center text-[10px] font-black uppercase tracking-[0.15em] text-zinc-600 border border-zinc-300 bg-white px-3 py-2 transition-all ${c.color}`}
                  data-testid={`share-channel-${c.id}`}
                  aria-label={`Condividi su ${c.label}`}
                >
                  {c.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

// ---------- Main Page ----------
export default function PublicStatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Host italiani in numeri — dati reali Locazione Turistica";
    // Set canonical + meta description
    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMeta('description', 'Dati aggregati reali della community Locazione Turistica: contratti firmati, prenotazioni gestite, schedine Alloggiati, entrate, città italiane coperte. Trasparenza totale sulla piattaforma per host Airbnb, Booking, B&B e case vacanze.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Host italiani in numeri — la community Locazione Turistica', 'property');
    setMeta('og:description', 'I numeri reali e aggiornati di chi gestisce affitti brevi con Locazione Turistica.', 'property');
    setMeta('og:type', 'website', 'property');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://contratti.vitadahost.it/stats';

    axios
      .get(`${API}/stats/public`)
      .then((r) => setStats(r.data))
      .catch((e) => console.error('Stats load failed', e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">
        Carico i numeri della community...
      </div>
    );
  }
  if (!stats) return null;

  const maxPlatformGross = Math.max(1, ...stats.bookings.platforms.map(p => p.gross || 0));
  const maxCity = Math.max(1, ...stats.geography.top_cities.map(c => c.count));
  const totalLangs = stats.contracts.languages.reduce((s, l) => s + l.count, 0) || 1;

  return (
    <div className="min-h-screen bg-white text-zinc-900 selection:bg-zinc-900 selection:text-white" data-testid="public-stats-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="stats-home-link">
            <Logo className="h-6 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/login"
              className="text-xs font-bold uppercase tracking-wider text-zinc-600 hover:text-zinc-900 transition-colors px-3 py-2"
            >
              Accedi
            </Link>
            <Link
              to="/register"
              className="text-xs font-black uppercase tracking-wider bg-zinc-900 text-white px-4 py-2 hover:bg-zinc-800 transition-colors"
              data-testid="stats-register-cta"
            >
              Prova gratis
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
        <div className="absolute inset-0 opacity-[0.035]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, black 1px, transparent 1px), radial-gradient(circle at 80% 70%, black 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <FadeIn>
            <div className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-600">
                Dati reali · aggiornati al {new Date(stats.generated_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-5">
              Host italiani<br />in numeri<span className="text-emerald-500">.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Trasparenza totale. Ecco cosa hanno fatto finora gli host che usano Locazione Turistica — contratti, prenotazioni, schedine, entrate gestite. Aggregati e anonimi, nessun dato personale.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Big KPIs */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="flex items-center gap-2 mb-6">
              <ChartLineUp size={16} weight="bold" className="text-zinc-400" />
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">I numeri principali</p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5">
            <KpiCard icon={Users} value={stats.hosts} label="Host registrati" accent="zinc" delay={0} />
            <KpiCard icon={FileText} value={stats.contracts.total} label="Contratti creati" accent="emerald" delay={100} />
            <KpiCard icon={Receipt} value={stats.receipts} label="Ricevute emesse" accent="indigo" delay={200} />
            <KpiCard icon={Shield} value={stats.schedine} label="Schedine scaricate" accent="rose" delay={300} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mt-3 sm:mt-5">
            <KpiCard icon={CurrencyEur} value={stats.impact.managed_revenue} prefix="€ " decimals={0} label="Entrate gestite" accent="emerald" delay={350} />
            <KpiCard icon={House} value={stats.properties} label="Proprietà gestite" accent="zinc" delay={400} />
            <KpiCard icon={MapPin} value={stats.geography.distinct_cities} label="Città italiane" accent="amber" delay={450} />
            <KpiCard icon={Calendar} value={stats.events.catalog} label="Eventi monitorati" accent="rose" delay={500} />
          </div>
        </div>
      </section>

      {/* Impact: hours saved + pages saved */}
      <section className="py-12 sm:py-20 bg-zinc-950 text-white px-4 sm:px-6 border-b border-zinc-200 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-400 mb-3">Il tempo vale più dei soldi</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-3">
              Quanto abbiamo semplificato<br />la vita agli host italiani
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 max-w-2xl mb-12">
              Stime conservative basate sui dati reali della piattaforma. Ogni contratto digitale elimina ~45 minuti di stampa, firma e archiviazione; ogni schedina automatizzata ~5 minuti sul portale Alloggiati; ogni ricevuta digitale ~8 minuti tra formato, stampa e bollo.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
            <FadeIn delay={100}>
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 p-7 sm:p-10 relative overflow-hidden group">
                <Clock size={36} weight="duotone" className="text-emerald-400 mb-5" />
                <p className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-3">
                  <CountUp target={stats.impact.hours_saved} />
                </p>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-400 mb-2">
                  Ore risparmiate
                </p>
                <p className="text-xs text-zinc-400 leading-relaxed max-w-sm">
                  Tempo che gli host hanno recuperato per il marketing, per gli ospiti, o semplicemente per loro stessi.
                </p>
                <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-colors duration-700" />
              </div>
            </FadeIn>

            <FadeIn delay={200}>
              <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 p-7 sm:p-10 relative overflow-hidden group">
                <Tree size={36} weight="duotone" className="text-emerald-400 mb-5" />
                <p className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-3">
                  <CountUp target={stats.impact.pages_saved} />
                </p>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-emerald-400 mb-2">
                  Fogli di carta risparmiati
                </p>
                <p className="text-xs text-zinc-400 leading-relaxed max-w-sm">
                  Documenti firmati digitalmente invece di stampati e archiviati. Meno carta, meno spreco, meno caos.
                </p>
                <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-colors duration-700" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Platforms breakdown */}
      {stats.bookings.platforms.length > 0 && (
        <section className="py-12 sm:py-20 px-4 sm:px-6 border-b border-zinc-200">
          <div className="max-w-6xl mx-auto">
            <FadeIn>
              <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400 mb-3">Dove prenotano gli ospiti</p>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3">
                Canali di prenotazione
              </h2>
              <p className="text-sm text-zinc-500 max-w-xl mb-10">
                Gli host italiani non dipendono da una sola piattaforma. Ecco la ripartizione delle prenotazioni registrate in contabilità.
              </p>
            </FadeIn>
            <div className="space-y-3 max-w-2xl">
              {stats.bookings.platforms.map((p, i) => (
                <FadeIn key={p.id} delay={i * 80}>
                  <div className="group" data-testid={`platform-row-${p.id}`}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <p className="text-sm font-black text-zinc-900">{p.label}</p>
                      <p className="text-xs font-bold text-zinc-500">
                        {p.count} {p.count === 1 ? 'prenotazione' : 'prenotazioni'} · € {p.gross.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="h-2 bg-zinc-100 overflow-hidden">
                      <div
                        className="h-full bg-zinc-900 transition-all duration-[1400ms] ease-out group-hover:bg-emerald-500"
                        style={{ width: `${Math.max(8, (p.gross / maxPlatformGross) * 100)}%` }}
                      />
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Languages */}
      {stats.contracts.languages.length > 0 && (
        <section className="py-12 sm:py-20 px-4 sm:px-6 border-b border-zinc-200 bg-zinc-50">
          <div className="max-w-6xl mx-auto">
            <FadeIn>
              <div className="flex items-center gap-2 mb-3">
                <Globe size={16} weight="bold" className="text-zinc-400" />
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">Contratti multilingua</p>
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3">
                Il mondo intero negli affitti brevi italiani
              </h2>
              <p className="text-sm text-zinc-500 max-w-xl mb-10">
                Ogni contratto è disponibile in 5 lingue. Ecco cosa scelgono gli ospiti della nostra community.
              </p>
            </FadeIn>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {stats.contracts.languages.map((l, i) => {
                const pct = Math.round((l.count / totalLangs) * 100);
                return (
                  <FadeIn key={l.code} delay={i * 80}>
                    <div className="bg-white border border-zinc-200 p-5 hover:border-zinc-900 transition-colors h-full">
                      <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500 mb-2">{l.code}</p>
                      <p className="text-3xl font-black text-zinc-900 mb-1">
                        <CountUp target={pct} suffix="%" />
                      </p>
                      <p className="text-xs text-zinc-500 leading-snug">{l.label}</p>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Top cities */}
      {stats.geography.top_cities.length > 0 && (
        <section className="py-12 sm:py-20 px-4 sm:px-6 border-b border-zinc-200">
          <div className="max-w-6xl mx-auto">
            <FadeIn>
              <div className="flex items-center gap-2 mb-3">
                <Buildings size={16} weight="bold" className="text-zinc-400" />
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">Dove siamo</p>
              </div>
              <h2 className="text-2xl sm:text-4xl font-black tracking-tight mb-3">
                Le città italiane dei nostri host
              </h2>
              <p className="text-sm text-zinc-500 max-w-xl mb-10">
                Dal Nord al Sud, passando dalle isole. Ogni città ha la sua community silenziosa di host che la fanno splendere.
              </p>
            </FadeIn>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {stats.geography.top_cities.map((c, i) => (
                <FadeIn key={c.city} delay={i * 60}>
                  <div className="flex items-center gap-4 bg-white border border-zinc-200 px-4 py-3 hover:border-zinc-900 transition-colors group">
                    <span className="text-xs font-black text-zinc-400 w-6">#{i + 1}</span>
                    <MapPin size={16} weight="duotone" className="text-zinc-400 group-hover:text-emerald-500 transition-colors" />
                    <p className="text-sm font-bold text-zinc-900 flex-1">{c.city}</p>
                    <div className="flex-1 h-1.5 bg-zinc-100">
                      <div
                        className="h-full bg-zinc-900 group-hover:bg-emerald-500 transition-all duration-[1200ms]"
                        style={{ width: `${(c.count / maxCity) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs font-bold text-zinc-500 w-10 text-right">{c.count}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Share block — prima del CTA finale */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto">
          <ShareBlock stats={stats} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-zinc-900 text-white px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5">
              Unisciti alla community<br /><span className="text-emerald-400">che ha già cambiato regole</span>
            </h2>
            <p className="text-sm sm:text-base text-zinc-400 leading-relaxed mb-8 max-w-xl mx-auto">
              7 giorni gratis, senza carta di credito. Se la community non fa per te, nessun problema — niente email di vendita, niente pressioni.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="group inline-flex items-center gap-2 bg-white text-zinc-900 font-black uppercase tracking-wider text-sm px-7 py-4 hover:bg-emerald-400 transition-colors"
              data-testid="stats-final-cta"
            >
              Inizia gratis ora
              <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-[11px] text-zinc-500 mt-5">
              I dati qui sopra si aggiornano ogni 2 ore. Nessun dato personale, nessun tracking individuale.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="py-8 px-4 sm:px-6 text-center text-xs text-zinc-400 border-t border-zinc-200">
        <Link to="/" className="hover:text-zinc-900 transition-colors">← Torna alla home</Link>
      </footer>
    </div>
  );
}
